import { Injectable } from '@angular/core'
import {
  Judge,
  JudgeDetails,
  JudgeProfile,
  PaginatedJudgeSummaries,
  UpdateJudgeProfileRequest,
} from '@ftr/annotations-contracts'
import { JudgeResponse } from '@ftr/annotations-contracts/v2'
import { NotFoundApiError, UnauthorizedApiError } from '@ftr/api-shared'
import { Uuid } from '@ftr/contracts/type/shared'
import { CourtSystemRegionCache } from '@ftr/data-court-system'
import { ApiResult, RemoteData } from '@ftr/foundation'
import { LoggingService } from '@ftr/ui-observability'
import { AnnotationsApiClientFactory, AnnotationsApiClientFactoryV2 } from '../../api-client'
import { BadRequestApiError } from '../../api-client/errors'
import { AnnotationsService } from '../base'

const MAX_JUDGE_SUMMARIES = 100

@Injectable({
  providedIn: 'root',
})
export class JudgesService extends AnnotationsService {
  constructor(
    apiClientFactory: AnnotationsApiClientFactory,
    regionCache: CourtSystemRegionCache,
    apiClientFactoryV2: AnnotationsApiClientFactoryV2, // v2 for new methods
    private readonly logger: LoggingService,
  ) {
    super(apiClientFactory, regionCache, apiClientFactoryV2)
  }

  getJudgeForUser(courtSystemId: Uuid): ApiResult<JudgeProfile | null> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client => client.judges.getJudgeForUser({ query: { courtSystemId } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          this.logger.error(`Failed to get judge for user in court system ${courtSystemId}`)
          return RemoteData.success(null)
        }

        return RemoteData.success<JudgeProfile | null>(result.body)
      },
    })
  }

  getJudgeSummaries({ courtSystemId }: { courtSystemId: Uuid }): ApiResult<PaginatedJudgeSummaries> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client =>
        client.judges.judgeSummaries({
          query: {
            courtSystemId,
            limit: MAX_JUDGE_SUMMARIES,
          },
        }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch judges'))
        }

        return RemoteData.success<PaginatedJudgeSummaries>(result.body)
      },
    })
  }

  getJudgeDetails({ judgeId, courtSystemId }: { judgeId: Uuid; courtSystemId: Uuid }): ApiResult<JudgeDetails> {
    return this.performApiRequest({
      courtSystemId,
      requestFn: client => client.judges.judgeDetails({ params: { judgeId } }),
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to fetch judges'))
        }

        return RemoteData.success<JudgeDetails>(result.body)
      },
    })
  }

  updateJudge(judge: UpdateJudgeProfileRequest): ApiResult<Judge> {
    return this.performApiRequest({
      courtSystemId: judge.courtSystemId,
      requestFn: client =>
        client.judges.updateJudge({
          body: judge,
        }),
      responseErrorMapFn: error => {
        if (error.message.includes('JudgeAlreadyExistsForUser')) {
          return RemoteData.failure(error)
        }

        return RemoteData.failure(new Error('Failed to update judge'))
      },
      responseMapFn: result => {
        if (result.status !== 200) {
          return RemoteData.failure(new Error('Failed to update judge'))
        }

        return RemoteData.success<Judge>(result.body)
      },
    })
  }

  getJudgesForCase({ caseId, courtSystemId }: { caseId: Uuid; courtSystemId: Uuid }): ApiResult<{
    items: JudgeResponse[]
    total: number
  }> {
    return this.performApiRequestV2({
      courtSystemId,
      requestFn: client => client.judges.listJudges({ params: { courtSystemId }, query: { caseId } }),
      responseMapFn: result => {
        if (result.status === 404) {
          return RemoteData.failure(new NotFoundApiError())
        }
        if (result.status === 401 || result.status === 403) {
          return RemoteData.failure(new UnauthorizedApiError())
        }
        if (result.status === 400) {
          return RemoteData.failure(new BadRequestApiError())
        }

        if (result.status === 200) {
          return RemoteData.success(result.body)
        }

        return RemoteData.failure(new Error('Unknown error'))
      },
    })
  }
}
